import { faChevronUp, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useRef  } from "react";
import { Form } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import {
    AccordionBody,
    AccordionCard,
    AccordionChevron,
    AccordionHeader,
    AccordionHeaderLeft,
    AccordionHeaderRight,
    AddDescriptionLink,
    AddOptionLink,
    AddQuestionButton,
    FieldLabel,
    FormInput,
    FormSelect,
    OptionRemoveButton,
    OptionRow,
    OptionsList,
    QuestionBody,
    QuestionCard,
    QuestionChevron,
    QuestionHeader,
    QuestionHeaderLeft,
    QuestionHeaderRight,
    QuestionRow,
    RemoveButton,
    RemoveButtonWrapper,
    RequiredMark,
    SectionField,
    ToggleContainer,
} from "./styles";
import {
    CustomQuestion,
    CustomSection,
    MAX_OPTIONS,
    OPTIONS_REQUIRED_TYPES,
    QUESTION_TYPE_OPTIONS,
    QuestionOption,
    QuestionType,
} from "./types";


const createDefaultQuestion = (): CustomQuestion => ({
    text: '',
    description: '',
    type: 'short_text',
    required: false,
    hiddenFromEvaluators: false,
    expanded: true,
    options: [],
})

const createDefaultOption = (): QuestionOption => ({
    id: uuidv4(),
    text: '',
})


const QuestionOptionsEditor = ({
    options,
    onChange,
}: {
    options: QuestionOption[]
    onChange: (updater: (options: QuestionOption[]) => QuestionOption[]) => void
}) => {
    const handleAddOption = () => {
        if (options.length >= MAX_OPTIONS) return
        onChange(prev => [...prev, createDefaultOption()])
    }

    const handleRemoveOption = (optionId: string) => {
        onChange(prev => prev.filter(o => o.id !== optionId))
    }

    const handleUpdateOption = (optionId: string, text: string) => {
        onChange(prev => prev.map(o => (o.id === optionId ? { ...o, text } : o)))
    }

    return (
        <>
            <OptionsList>
                {options.map((option, index) => (
                    <OptionRow key={option.id}>
                        <SectionField>
                            <FieldLabel>Opção {index + 1}</FieldLabel>
                            <FormInput
                                type="text"
                                value={option.text}
                                placeholder="Digite a descrição do critério"
                                onChange={e => handleUpdateOption(option.id, e.target.value)}
                            />
                        </SectionField>
                        <OptionRemoveButton
                            type="button"
                            aria-label={`Remover opção ${index + 1}`}
                            onClick={() => handleRemoveOption(option.id)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </OptionRemoveButton>
                    </OptionRow>
                ))}
            </OptionsList>

            {options.length < MAX_OPTIONS && (
                <AddOptionLink type="button" onClick={handleAddOption}>
                    <FontAwesomeIcon icon={faPlus} />
                    Adicionar opção
                </AddOptionLink>
            )}
        </>
    )
}

interface SectionCardProps {
    section: CustomSection
    sectionIndex: number
    onToggle: () => void
    onChange: (updater: (section: CustomSection) => CustomSection) => void
    onChangeQuestion: (
        questionIndex: number,
        updater: (question: CustomQuestion) => CustomQuestion,
    ) => void
    onRemove: () => void
}

const QuestionItem = ({
    question,
    questionIndex,
    onChange,
    onRemove,
}: {
    question: CustomQuestion
    questionIndex: number
    onChange: (updater: (question: CustomQuestion) => CustomQuestion) => void
    onRemove: () => void
}) => {
    const toggle = () => onChange(q => ({ ...q, expanded: !q.expanded }))

    return (
        <QuestionCard data-testid="question-card">
            <QuestionHeader
                role="button"
                tabIndex={0}
                onClick={toggle}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggle()
                    }
                }}
            >
                <QuestionHeaderLeft>
                    <QuestionChevron open={!!question.expanded}>
                        <FontAwesomeIcon icon={faChevronUp} />
                    </QuestionChevron>
                    <span>Pergunta {questionIndex + 1}</span>
                </QuestionHeaderLeft>
                <QuestionHeaderRight onClick={e => e.stopPropagation()}>
                    <RemoveButtonWrapper>
                        <RemoveButton
                            size="sm"
                            variant="outline-secondary"
                            onClick={onRemove}
                            aria-label="Remover pergunta"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </RemoveButton>
                    </RemoveButtonWrapper>
                </QuestionHeaderRight>
            </QuestionHeader>

            {question.expanded && (
                <QuestionBody>
                    <QuestionRow>
                        <SectionField>
                            <FieldLabel>
                                Nome da pergunta{question.required && <RequiredMark>*</RequiredMark>}
                            </FieldLabel>
                            <FormInput
                                type="text"
                                value={question.text}
                                onChange={e => onChange(q => ({ ...q, text: e.target.value }))}
                            />
                        </SectionField>
                        <SectionField>
                            <FieldLabel>Tipo de pergunta</FieldLabel>
                            <FormSelect
                                value={question.type}
                                onChange={e =>
                                    onChange(q => ({ ...q, type: e.target.value as QuestionType }))
                                }
                            >
                                {QUESTION_TYPE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </FormSelect>
                        </SectionField>
                    </QuestionRow>

                    {question.description ? (
                        <SectionField style={{ marginBottom: 12 }}>
                            <FormInput
                                type="text"
                                value={question.description}
                                placeholder="Descrição da pergunta"
                                onChange={e =>
                                    onChange(q => ({ ...q, description: e.target.value }))
                                }
                            />
                        </SectionField>
                    ) : (
                        <AddDescriptionLink
                            type="button"
                            onClick={() => onChange(q => ({ ...q, description: ' ' }))}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Adicionar Descrição
                        </AddDescriptionLink>
                    )}

                    {OPTIONS_REQUIRED_TYPES.includes(question.type) && (
                        <QuestionOptionsEditor
                            options={question.options ?? []}
                            onChange={updater =>
                                onChange(q => ({ ...q, options: updater(q.options ?? []) }))
                            }
                        />
                    )}

                    <ToggleContainer>
                        <Form.Check
                            type="switch"
                            id={`required-${questionIndex}`}
                            label="A pergunta obrigatória?"
                            checked={!!question.required}
                            onChange={e =>
                                onChange(q => ({ ...q, required: e.target.checked }))
                            }
                        />
                    </ToggleContainer>
                </QuestionBody>
            )}
        </QuestionCard>
    )
}


export const SectionCard = ({
    section,
    sectionIndex,
    onToggle,
    onChange,
    onChangeQuestion,
    onRemove,
}: SectionCardProps) => {

    const handleAddQuestion = () =>
        onChange(s => ({ ...s, questions: [...s.questions, createDefaultQuestion()] }))

    const handleRemoveQuestion = (questionIndex: number) =>
        onChange(s => ({
            ...s,
            questions: s.questions.length > 1
                ? s.questions.filter((_, i) => i !== questionIndex)
                : s.questions,
        }))

    const handleRemoveSection = () => {
        onRemove()
    }

    return (
        <AccordionCard data-testid="section-card">
            <AccordionHeader
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onToggle()
                    }
                }}
                data-testid="section-toggle"
                aria-label={`Alternar seção ${sectionIndex + 1}`}
            >
                <AccordionHeaderLeft>
                    <AccordionChevron open={section.expanded}>
                        <FontAwesomeIcon icon={faChevronUp} />
                    </AccordionChevron>
                    <span>Seção {sectionIndex + 1}</span>
                </AccordionHeaderLeft>
                <AccordionHeaderRight onClick={e => e.stopPropagation()}>
                    <RemoveButtonWrapper>
                        <RemoveButton
                            size="sm"
                            variant="outline-secondary"
                            onClick={handleRemoveSection}
                            aria-label="Remover seção"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </RemoveButton>
                    </RemoveButtonWrapper>
                </AccordionHeaderRight>
            </AccordionHeader>

            {section.expanded && (
                <AccordionBody>
                    <SectionField style={{ marginBottom: 20 }}>
                        <FieldLabel htmlFor={`section-name-${sectionIndex}`}>
                            Nome da seção<RequiredMark>*</RequiredMark>
                        </FieldLabel>
                        <FormInput
                            id={`section-name-${sectionIndex}`}
                            type="text"
                            value={section.name}
                            onChange={e => onChange(s => ({ ...s, name: e.target.value }))}
                        />
                    </SectionField>

                    {section.questions.map((question, questionIndex) => (
                        <QuestionItem
                            key={question.id || questionIndex}
                            question={question}
                            questionIndex={questionIndex}
                            onChange={updater => onChangeQuestion(questionIndex, updater)}
                            onRemove={() => handleRemoveQuestion(questionIndex)}
                        />
                    ))}

                    <AddQuestionButton type="button" onClick={handleAddQuestion} data-testid="add-question-button">
                        <FontAwesomeIcon icon={faPlus} />
                        Adicionar pergunta
                    </AddQuestionButton>
                </AccordionBody>
            )}
        </AccordionCard>
    )
}
